import Vue from 'vue'
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import constants from '@/constant'

import SlideApi from '@/api/slide'
import SlideOptionApi from '@/api/slideOption'
import utils from '@/util'
import PresentationUtils from '@/utils/presentation'
import EventBus from '@/eventBus'

const { eventCategories } = constants

export default (VotingIdeasContent) => Vue.component('VotingIdeasContentContainer', {

  data() {
    return {
      tabName: constants.IdeasTabName,
      showIdea: false,
      itemPicked: {},
      masonryTransitionDuration: 500,
      scrollShow: false,
      showArrowScrollTop: false,
      showArrowScrollBottom: true,
      scrollDisable: false,
      ideasResultItemBorder: '#D1D1D1',
      masonryContainter: '#ideas-container',
      loadedIdeas: false,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
    }),
    ...mapGetters(['isTextColourLight']),
    ...mapGetters('slideModule', ['modalBackgroundColour']),
    ...mapGetters('presentationSetting', ['isShownEmojiAvatar', 'canUseCollectParticipantInfo']),

    isVotingStepSubmission() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.SUBMISSION
    },
    isVotingStepVoting() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.VOTING
    },
    isVotingStepResult() {
      return this.slideActive.type === 'ideas' && this.slideActive.votingStep === this.tabName.RESULT
    },
    isVoteCountTextVisible() {
      return (this.isVotingStepVoting && this.slideActive.showVotes) || this.isVotingStepResult
    },
    arrowScrollTopVisible() {
      return this.scrollShow && this.showArrowScrollTop
    },
    arrowScrollBottomVisible() {
      return this.scrollShow && this.showArrowScrollBottom
    },
    isBlurAnswerContent() {
      return !this.slideActive.showSubmissions && this.isVotingStepSubmission
    },
  },
  watch: {
    '$store.state.slideActive.votingStep': function watchVotingStep() {
      this._handleScrollingAndShowScroll()
      if (this.isVotingStepResult) {
        this.handleRankIdeasByVotes()
      } else if (this.isVotingStepSubmission || this.isVotingStepVoting) {
        this.handleSortIdeasByCreatedAt()
      }
    },
  },
  mounted() {
    EventBus.$on('handleSortIdeasByCreatedAt', () => {
      this.handleSortIdeasByCreatedAt()
    })
    EventBus.$on('handleRankIdeasByVotes', () => {
      this.handleRankIdeasByVotes()
    })
    EventBus.$on('handleScrollingAndShowScroll', () => {
      this._handleScrollingAndShowScroll()
    })
    EventBus.$on('handleRemovePickOption', () => {
      this.removePickOption()
    })
    if (this.slideActive.hasPickedSubmission) {
      this.updateSlideActiveAction({
        hasPickedSubmission: false,
      })
    }

    EventBus.$on('slide-handle-enter', () => {
      if (this.slideActive.votingStep === this.tabName.SUBMISSION && this.slideActive.SlideOptions.length) {
        this.selectTab(this.tabName.VOTING)
      } else if (this.slideActive.votingStep === this.tabName.VOTING) {
        this.selectTab(this.tabName.RESULT)
      }
    })
    this.getIdeas()
  },
  destroyed() {
    EventBus.$off('handleSortIdeasByCreatedAt')
    EventBus.$off('handleRankIdeasByVotes')
    EventBus.$off('handleScrollingAndShowScroll')
    EventBus.$off('handleRemovePickOption')
    EventBus.$off('slide-handle-enter')
    utils.allowPDFRender()
  },
  methods: {
    ...mapActions(['updateSlideActiveAction', 'setSyncLoadingAction']),
    async getIdeas() {
      this.loadedIdeas = false
      try {
        const slideId = this.slideActive.id
        const resp = await SlideOptionApi.getSlideOptions({
          slideId,
          pageSize: constants.MAX_IDEAS_NUMBER,
        })
        const payload = {
          slideId,
          SlideOptions: resp.rows,
        }

        this.updateSlideActiveAction(payload)
      } catch (e) {
        Sentry.captureException(e)
        this.$store.dispatch('setSyncPresentation', 3)
      }
      this.handleSortIdeasByCreatedAt()
      if (this.isVotingStepResult) {
        this.handleRankIdeasByVotes()
      }
      this.loadedIdeas = true
      this._checkMaxSubmission()
      this._handleScrollingAndShowScroll()
    },
    getSumVoteCount() {
      if (this.slideActive.SlideOptions) {
        return this.slideActive.SlideOptions.reduce((a, b) => a + (b.votesCount || 0), 0)
      }
      return 0
    },
    _checkMaxSubmission() {
      if (this.slideActive.SlideOptions.length > constants.MAX_IDEAS_NUMBER) {
        this.slideActive.SlideOptions = this.slideActive.SlideOptions.slice(0, 200)
        this._updateSlideActive()
      }
    },
    _handleScrollingAndShowScroll() {
      setTimeout(() => {
        const container = this.$el.querySelector(this.masonryContainter)
        if (container && container.scrollHeight > container.clientHeight) {
          this.scrollShow = true
        } else {
          this.scrollShow = false
        }
        if (container && !container.onscroll) {
          container.onscroll = () => {
            this._handleScroll(container)
          }
        }
      }, 1200)
    },
    async selectTab(tabName) {
      if (this.$store.state.sharePresentation) return
      this._hideQuestionImage()
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Brainstorm > Change voting step',
        `From step: ${this.slideActive.votingStep} to step: ${tabName}`,
      )
      this.slideActive.votingStep = tabName
      this.updateSlideActiveAction(this.slideActive)
      if (this.isVotingStepResult) {
        const container = this.$el.querySelector(this.masonryContainter)
        if (container && container.scrollTop) {
          container.scrollTop = 0
        }
      }
      if (this.isVotingStepVoting || this.isVotingStepResult) {
        this._trackGAWhenEnableAllSteps()
      }
      const payload = {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        votingStep: tabName,
      }
      this.setSyncLoadingAction(1)
      try {
        await SlideApi.patchSlideById(this.slideActive.id, payload)
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Sentry.captureException(error, `Brainstorm: Update votingStep to '${payload.votingStep}' failed.`)
      }
    },

    _trackGAWhenEnableAllSteps() {
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Brainstorm > Change voting step > Enable all steps',
      )
    },

    async toggleShowSubmission() {
      this.setSyncLoadingAction(1)
      this.slideActive.showSubmissions = !this.slideActive.showSubmissions
      try {
        await SlideApi.patchSlideById(this.slideActive.id, {
          showSubmissions: this.slideActive.showSubmissions,
        })
        this._updateSlideActive()
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Sentry.captureException(error, 'Brainstorm: Show/hide submission failed.')
      }

      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Brainstorm > Step 1: Submission > Show / hide submission',
        `Show submission: ${this.slideActive.showSubmissions}`,
      )
    },

    async toggleShowVotes() {
      this.setSyncLoadingAction(1)
      this.slideActive.showVotes = !this.slideActive.showVotes
      try {
        await SlideApi.patchSlideById(this.slideActive.id, {
          showVotes: this.slideActive.showVotes,
        })
        this._updateSlideActive()
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Sentry.captureException(error, 'Brainstorm: Show/hide votes failed.')
      }
      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Brainstorm > Step 2: Voting > Show / hide vote counts',
        `Show vote counts: ${this.slideActive.showVotes}`,
      )
    },

    pickOption(item, index) {
      this.showIdea = true
      this.itemPicked = item
      this.itemPicked.indexOption = index
      this.slideActive.hasPickedSubmission = true
      this.updateSlideActiveAction({
        hasPickedSubmission: true,
      })
    },

    removePickOption() {
      this.itemPicked = {}
      this.slideActive.hasPickedSubmission = false
      this.updateSlideActiveAction({
        hasPickedSubmission: false,
      })
      setTimeout(() => {
        this.showIdea = false
      }, 200)
    },

    _hideQuestionImage() {
      this.$store.dispatch('updateSlideActive', {
        showQuestionImage: false,
      })
    },

    checkShowAudienceInfo(idea) {
      return utils.checkShowAudienceInfo(idea)
    },

    checkShowAudienceFields(fieldName, audienceInfo) {
      return utils.checkShowAudienceFields(fieldName, audienceInfo)
    },

    setToColorPalette(item, index) {
      return utils.setToColorPalette(item, index)
    },

    _handleScroll(container) {
      utils.disallowPDFRender()

      if (container.scrollHeight <= container.clientHeight) {
        this.showArrowScrollTop = false
        this.showArrowScrollBottom = false
        return
      }

      if (container.scrollTop === 0) {
        this.showArrowScrollTop = false
        this.showArrowScrollBottom = true
        return
      }

      if (container.scrollHeight <= Math.ceil(container.scrollTop + container.clientHeight)) {
        this.showArrowScrollTop = true
        this.showArrowScrollBottom = false
        return
      }

      this.showArrowScrollTop = true
      this.showArrowScrollBottom = true
      setTimeout(() => {
        utils.allowPDFRender()
      }, this.masonryTransitionDuration + 2000)
    },

    scrollTop() {
      const container = this.$el.querySelector(this.masonryContainter)
      if (!container) return

      this.scrollDisable = false

      utils.scrollToElement(container, {
        behavior: 'smooth',
        top: container.scrollTop < 0 ? 0 : container.scrollTop - 70,
      })
    },

    scrollBottom() {
      const container = this.$el.querySelector(this.masonryContainter)
      if (!container) return

      utils.scrollToElement(container, {
        behavior: 'smooth',
        top: container.scrollTop + 70,
      })

      if (
        container.scrollTop + container.clientHeight ===
        container.scrollHeight
      ) {
        this.scrollDisable = true
      }
    },

    handleRankIdeasByVotes() {
      if (this.slideActive.SlideOptions && this.slideActive.SlideOptions.length) {
        const sortedIdeas = this.slideActive.SlideOptions.sort((a, b) => {
          return b.votesCount - a.votesCount
        })
        sortedIdeas.forEach((idea, index) => {
          if (index === 0) {
            idea.rankOrder = 1
            return
          }

          const previousIdea = sortedIdeas[index - 1]
          if (!previousIdea.votesCount) previousIdea.votesCount = 0
          if (idea.votesCount === previousIdea.votesCount) {
            idea.rankOrder = previousIdea.rankOrder
          } else {
            idea.rankOrder = index + 1
          }
        })
        const payload = {
          SlideOptions: sortedIdeas,
        }
        this.$store.dispatch('updateSlideActive', payload)
        EventBus.$emit('on-reDraw-masonry')
      }
    },

    handleSortIdeasByCreatedAt() {
      if (this.slideActive.SlideOptions && this.slideActive.SlideOptions.length) {
        this.slideActive.SlideOptions.sort((a, b) => {
          return new Date(a.createdAt) - new Date(b.createdAt)
        })
        this.slideActive.SlideOptions.forEach((idea, index) => {
          idea.createdAtOrder = index + 1
        })
        this._updateSlideActive()
        EventBus.$emit('on-reDraw-masonry')
      }
    },

    _updateSlideActive() {
      this.updateSlideActiveAction(this.slideActive)
    },

    _handleUpdatePresentationHasResults() {
      if (this.slideActive.SlideOptions && this.slideActive.SlideOptions.length === 0) {
        PresentationUtils.checkAndUpdatePresentationHasResults([this.slideActive.id])
      }
    },

    async handleRemoveResponse(idea, index) {
      if (this.isVotingStepVoting || this.isVotingStepResult) return

      this.slideActive.SlideOptions = this.slideActive.SlideOptions.map((item) => {
        return item.id === idea.id ? { ...item, deleting: true } : item
      })

      const payload = {
        presentationId: this.presentation.id,
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      }
      this.setSyncLoadingAction(1)
      try {
        await SlideOptionApi.deleteSlideOption(idea.id, payload)
        this.slideActive.SlideOptions.splice(index, 1)
        this.handleSortIdeasByCreatedAt()
        this._handleUpdatePresentationHasResults()
        this._handleScrollingAndShowScroll()
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Brainstorm > Remove idea > Removed successfully',
        )
        this.setSyncLoadingAction(2)
      } catch (error) {
        this.setSyncLoadingAction(3)
        Sentry.captureException(error, `Brainstorm: Remove idea ${idea.id} failed.`)
      } finally {
        this.slideActive.SlideOptions = this.slideActive.SlideOptions.map((item) => {
          return item.id === idea.id ? { ...item, deleting: false } : item
        })
      }
    },
    setBoxShadowValue(item, index) {
      return `0 0 0 1.5px ${utils.setToColorPalette(item, index)}`
    },
    getRankOrderBg() {
      return utils.hexToRgba(this.modalBackgroundColour)
    },
  },
  render(createElement) {
    return createElement(VotingIdeasContent, {
      props: {
        slideActive: this.slideActive,
        masonryTransitionDuration: this.masonryTransitionDuration,
        tabName: this.tabName,
        getSumVoteCount: this.getSumVoteCount,
        selectTab: this.selectTab,
        toggleShowSubmission: this.toggleShowSubmission,
        toggleShowVotes: this.toggleShowVotes,
        pickOption: this.pickOption,
        removePickOption: this.removePickOption,
        itemPicked: this.itemPicked,
        showIdea: this.showIdea,
        checkShowAudienceInfo: this.checkShowAudienceInfo,
        checkShowAudienceFields: this.checkShowAudienceFields,
        setToColorPalette: this.setToColorPalette,
        scrollShow: this.scrollShow,
        scrollDisable: this.scrollDisable,
        scrollTop: this.scrollTop,
        scrollBottom: this.scrollBottom,
        isVotingStepResult: this.isVotingStepResult,
        handleRemoveResponse: this.handleRemoveResponse,
        isVotingStepSubmission: this.isVotingStepSubmission,
        isVoteCountTextVisible: this.isVoteCountTextVisible,
        arrowScrollTopVisible: this.arrowScrollTopVisible,
        arrowScrollBottomVisible: this.arrowScrollBottomVisible,
        setBoxShadowValue: this.setBoxShadowValue,
        getRankOrderBg: this.getRankOrderBg,
        isBlurAnswerContent: this.isBlurAnswerContent,
        loadedIdeas: this.loadedIdeas,
        isTextColourLight: this.isTextColourLight,
        modalBackgroundColour: this.modalBackgroundColour,
        isShownEmojiAvatar: this.isShownEmojiAvatar,
        canUseCollectParticipantInfo: this.canUseCollectParticipantInfo,
      },
    })
  },
})
